import type {
  Bullet,
  DiagramEdge,
  DiagramEdgeKind,
  DiagramNode,
  DiagramNodeKind,
  Fiche,
  FicheColor,
  FicheSection,
  Patch,
  TemplateId,
} from '@pharmafiche/schema';
import * as jsonpatch from 'fast-json-patch';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

function makeId(prefix: string): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `${prefix}-${crypto.randomUUID().slice(0, 8)}`;
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

interface EditorState {
  fiche: Fiche | null;
  /** Past snapshots (most recent last) for undo. */
  past: Fiche[];
  /** Future snapshots (most recent last) for redo. */
  future: Fiche[];
  /** Currently focused section for scoped AI edits. Cleared on outside-click. */
  focusedSectionId: string | null;
}

interface EditorActions {
  loadFiche: (fiche: Fiche) => void;
  unload: () => void;

  undo: () => void;
  redo: () => void;
  /** Clear past + future stacks. */
  clearHistory: () => void;

  /** Focus management for scoped AI edits. */
  setFocusedSection: (sectionId: string | null) => void;

  /** Apply an RFC 6902 patch from the AI; snapshots history first. */
  applyPatch: (patch: Patch) => { ok: true } | { ok: false; error: string };

  // Meta
  updateMetaTitle: (title: string) => void;
  updateMetaSubtitle: (subtitle: string) => void;
  setTemplate: (id: TemplateId) => void;

  // Sections — general
  updateSectionTitle: (sectionId: string, title: string) => void;
  updateSectionWarning: (sectionId: string, warning: string) => void;
  recolorSection: (sectionId: string, color: FicheColor) => void;
  moveSection: (sectionId: string, dir: 'up' | 'down') => void;
  reorderSections: (fromIndex: number, toIndex: number) => void;
  duplicateSection: (sectionId: string) => void;
  removeSection: (sectionId: string) => void;

  // Bullets
  updateBulletText: (sectionId: string, bulletId: string, text: string) => void;
  addBulletAfter: (sectionId: string, afterBulletId: string | null) => void;
  removeBullet: (sectionId: string, bulletId: string) => void;

  // Drug card
  updateDrugName: (sectionId: string, name: string) => void;
  updateDrugSubsectionLabel: (sectionId: string, subId: string, label: string) => void;
  updateDrugSubsectionBulletText: (
    sectionId: string,
    subId: string,
    bulletId: string,
    text: string,
  ) => void;

  // Mnemonic / warning / case
  updateMnemonicText: (sectionId: string, text: string) => void;
  updateWarningText: (sectionId: string, text: string) => void;
  updateClinicalPatient: (sectionId: string, text: string) => void;
  updateClinicalQuestion: (sectionId: string, text: string) => void;

  // Diagram editing
  addDiagramNode: (diagramId: string, node: Omit<DiagramNode, 'id'> & { id?: string }) => string;
  updateDiagramNodePosition: (diagramId: string, nodeId: string, x: number, y: number) => void;
  updateDiagramNodeLabel: (diagramId: string, nodeId: string, label: string) => void;
  updateDiagramNodeKind: (diagramId: string, nodeId: string, kind: DiagramNodeKind) => void;
  updateDiagramNodeColor: (diagramId: string, nodeId: string, color: FicheColor) => void;
  removeDiagramNode: (diagramId: string, nodeId: string) => void;
  addDiagramEdge: (
    diagramId: string,
    edge: Omit<DiagramEdge, 'id'> & { id?: string },
  ) => string | null;
  updateDiagramEdgeKind: (diagramId: string, edgeId: string, kind: DiagramEdgeKind) => void;
  removeDiagramEdge: (diagramId: string, edgeId: string) => void;
  updateDiagramTitle: (diagramId: string, title: string) => void;
}

export type EditorStore = EditorState & EditorActions;

const HISTORY_LIMIT = 50;

function findSection(fiche: Fiche, id: string): FicheSection | undefined {
  return fiche.sections.find((s) => s.id === id);
}

function findBullet(bullets: Bullet[], id: string): Bullet | undefined {
  return bullets.find((b) => b.id === id);
}

/**
 * Push the current fiche onto the past stack BEFORE mutating, and clear future.
 * Returns true if the snapshot was pushed (i.e. there was a fiche to snapshot).
 */
function snapshot(draft: { fiche: Fiche | null; past: Fiche[]; future: Fiche[] }): boolean {
  if (!draft.fiche) return false;
  // Deep clone via structuredClone (Workers + modern browsers + jsdom).
  // Falls back to JSON round-trip if structuredClone isn't available.
  // JSON round-trip — structuredClone can't traverse Immer proxies.
  const copy = JSON.parse(JSON.stringify(draft.fiche)) as Fiche;
  draft.past.push(copy);
  if (draft.past.length > HISTORY_LIMIT) draft.past.shift();
  draft.future = [];
  draft.fiche.meta.updated_at = new Date().toISOString();
  return true;
}

export const useEditorStore = create<EditorStore>()(
  immer<EditorStore>((set, get) => ({
    fiche: null,
    past: [],
    future: [],
    focusedSectionId: null,

    loadFiche: (fiche) =>
      set((s) => {
        s.fiche = fiche;
        s.past = [];
        s.future = [];
        s.focusedSectionId = null;
      }),

    unload: () =>
      set((s) => {
        s.fiche = null;
        s.past = [];
        s.future = [];
        s.focusedSectionId = null;
      }),

    clearHistory: () =>
      set((s) => {
        s.past = [];
        s.future = [];
      }),

    setFocusedSection: (sectionId) =>
      set((s) => {
        s.focusedSectionId = sectionId;
      }),

    applyPatch: (patch) => {
      const current = get().fiche;
      if (!current) return { ok: false, error: 'no_fiche' };
      try {
        // Apply against a plain JSON clone to avoid mutating the live store and
        // to keep fast-json-patch away from Immer proxies.
        const clone = JSON.parse(JSON.stringify(current)) as Fiche;
        const result = jsonpatch.applyPatch(clone, patch as jsonpatch.Operation[], true, false);
        const next = result.newDocument as Fiche;
        set((s) => {
          if (!s.fiche) return;
          // snapshot() pushes the pre-patch state onto past
          snapshot(s);
          s.fiche = next;
        });
        return { ok: true };
      } catch (err) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : 'apply_failed',
        };
      }
    },

    undo: () =>
      set((s) => {
        if (!s.fiche) return;
        const prev = s.past.pop();
        if (!prev) return;
        // Push CURRENT onto future before swapping in prev
        const curCopy: Fiche = JSON.parse(JSON.stringify(s.fiche));
        s.future.push(curCopy);
        s.fiche = prev;
      }),

    redo: () =>
      set((s) => {
        if (!s.fiche) return;
        const next = s.future.pop();
        if (!next) return;
        const curCopy: Fiche = JSON.parse(JSON.stringify(s.fiche));
        s.past.push(curCopy);
        if (s.past.length > HISTORY_LIMIT) s.past.shift();
        s.fiche = next;
      }),

    updateMetaTitle: (title) =>
      set((s) => {
        if (!s.fiche) return;
        if (s.fiche.meta.title === title) return;
        snapshot(s);
        s.fiche.meta.title = title;
      }),

    updateMetaSubtitle: (subtitle) =>
      set((s) => {
        if (!s.fiche) return;
        if ((s.fiche.meta.subtitle ?? '') === subtitle) return;
        snapshot(s);
        s.fiche.meta.subtitle = subtitle || undefined;
      }),

    setTemplate: (id) =>
      set((s) => {
        if (!s.fiche) return;
        if (s.fiche.meta.template_id === id) return;
        snapshot(s);
        s.fiche.meta.template_id = id;
      }),

    updateSectionTitle: (sectionId, title) =>
      set((s) => {
        if (!s.fiche) return;
        const sec = findSection(s.fiche, sectionId);
        if (!sec) return;
        if (
          sec.type === 'intro' ||
          sec.type === 'classification' ||
          sec.type === 'comparison_table' ||
          sec.type === 'free_text'
        ) {
          if (sec.title === title) return;
          snapshot(s);
          // Re-find on the draft (snapshot replaced past)
          const s2 = findSection(s.fiche, sectionId);
          if (
            s2 &&
            (s2.type === 'intro' ||
              s2.type === 'classification' ||
              s2.type === 'comparison_table' ||
              s2.type === 'free_text')
          ) {
            s2.title = title;
          }
        }
      }),

    updateSectionWarning: (sectionId, warning) =>
      set((s) => {
        if (!s.fiche) return;
        const sec = findSection(s.fiche, sectionId);
        if (!sec) return;
        if (sec.type === 'intro') {
          snapshot(s);
          const s2 = findSection(s.fiche, sectionId);
          if (s2?.type === 'intro') s2.warning = warning || undefined;
        } else if (sec.type === 'classification') {
          snapshot(s);
          const s2 = findSection(s.fiche, sectionId);
          if (s2?.type === 'classification') s2.note = warning || undefined;
        }
      }),

    recolorSection: (sectionId, color) =>
      set((s) => {
        if (!s.fiche) return;
        const sec = findSection(s.fiche, sectionId);
        if (!sec) return;
        if (sec.type === 'intro' || sec.type === 'classification') {
          if (sec.color === color) return;
          snapshot(s);
          const s2 = findSection(s.fiche, sectionId);
          if (s2 && (s2.type === 'intro' || s2.type === 'classification')) s2.color = color;
        } else if (sec.type === 'drug_card') {
          if (sec.drug.color === color) return;
          snapshot(s);
          const s2 = findSection(s.fiche, sectionId);
          if (s2?.type === 'drug_card') s2.drug.color = color;
        }
      }),

    moveSection: (sectionId, dir) =>
      set((s) => {
        if (!s.fiche) return;
        const i = s.fiche.sections.findIndex((sec) => sec.id === sectionId);
        if (i < 0) return;
        const j = dir === 'up' ? i - 1 : i + 1;
        if (j < 0 || j >= s.fiche.sections.length) return;
        snapshot(s);
        const a = s.fiche.sections[i];
        const b = s.fiche.sections[j];
        if (!a || !b) return;
        s.fiche.sections[i] = b;
        s.fiche.sections[j] = a;
      }),

    reorderSections: (fromIndex, toIndex) =>
      set((s) => {
        if (!s.fiche) return;
        if (fromIndex === toIndex) return;
        if (fromIndex < 0 || fromIndex >= s.fiche.sections.length) return;
        if (toIndex < 0 || toIndex >= s.fiche.sections.length) return;
        snapshot(s);
        const [moved] = s.fiche.sections.splice(fromIndex, 1);
        if (moved) s.fiche.sections.splice(toIndex, 0, moved);
      }),

    duplicateSection: (sectionId) =>
      set((s) => {
        if (!s.fiche) return;
        const i = s.fiche.sections.findIndex((sec) => sec.id === sectionId);
        if (i < 0) return;
        const original = s.fiche.sections[i];
        if (!original) return;
        snapshot(s);
        const clone = JSON.parse(JSON.stringify(original)) as FicheSection;
        clone.id = makeId('sec');
        reidNestedBullets(clone);
        s.fiche.sections.splice(i + 1, 0, clone);
      }),

    removeSection: (sectionId) =>
      set((s) => {
        if (!s.fiche) return;
        if (!s.fiche.sections.some((sec) => sec.id === sectionId)) return;
        snapshot(s);
        s.fiche.sections = s.fiche.sections.filter((sec) => sec.id !== sectionId);
      }),

    updateBulletText: (sectionId, bulletId, text) =>
      set((s) => {
        if (!s.fiche) return;
        const sec = findSection(s.fiche, sectionId);
        if (!sec) return;
        let target: Bullet | undefined;
        if (sec.type === 'intro' || sec.type === 'classification') {
          target = findBullet(sec.bullets, bulletId);
        } else if (sec.type === 'clinical_case') {
          target = findBullet(sec.conduite, bulletId);
        } else if (sec.type === 'drug_card') {
          target = findBullet(sec.drug.intro_bullets, bulletId);
        }
        if (!target) return;
        if (target.text === text) return;
        snapshot(s);
        // Re-find on the post-snapshot draft and apply
        const sec2 = findSection(s.fiche, sectionId);
        if (!sec2) return;
        if (sec2.type === 'intro' || sec2.type === 'classification') {
          const b = findBullet(sec2.bullets, bulletId);
          if (b) b.text = text;
        } else if (sec2.type === 'clinical_case') {
          const b = findBullet(sec2.conduite, bulletId);
          if (b) b.text = text;
        } else if (sec2.type === 'drug_card') {
          const b = findBullet(sec2.drug.intro_bullets, bulletId);
          if (b) b.text = text;
        }
      }),

    addBulletAfter: (sectionId, afterBulletId) =>
      set((s) => {
        if (!s.fiche) return;
        const sec = findSection(s.fiche, sectionId);
        if (!sec) return;
        if (sec.type !== 'intro' && sec.type !== 'classification') return;
        snapshot(s);
        const newBullet: Bullet = { id: makeId('b'), text: '' };
        const sec2 = findSection(s.fiche, sectionId);
        if (!sec2 || (sec2.type !== 'intro' && sec2.type !== 'classification')) return;
        const idx = afterBulletId
          ? sec2.bullets.findIndex((b) => b.id === afterBulletId)
          : sec2.bullets.length - 1;
        sec2.bullets.splice((idx < 0 ? sec2.bullets.length - 1 : idx) + 1, 0, newBullet);
      }),

    removeBullet: (sectionId, bulletId) =>
      set((s) => {
        if (!s.fiche) return;
        const sec = findSection(s.fiche, sectionId);
        if (!sec) return;
        if (sec.type !== 'intro' && sec.type !== 'classification') return;
        if (!sec.bullets.some((b) => b.id === bulletId)) return;
        snapshot(s);
        const sec2 = findSection(s.fiche, sectionId);
        if (sec2 && (sec2.type === 'intro' || sec2.type === 'classification')) {
          sec2.bullets = sec2.bullets.filter((b) => b.id !== bulletId);
        }
      }),

    updateDrugName: (sectionId, name) =>
      set((s) => {
        if (!s.fiche) return;
        const sec = findSection(s.fiche, sectionId);
        if (sec?.type !== 'drug_card') return;
        if (sec.drug.name === name) return;
        snapshot(s);
        const sec2 = findSection(s.fiche, sectionId);
        if (sec2?.type === 'drug_card') sec2.drug.name = name;
      }),

    updateDrugSubsectionLabel: (sectionId, subId, label) =>
      set((s) => {
        if (!s.fiche) return;
        const sec = findSection(s.fiche, sectionId);
        if (sec?.type !== 'drug_card') return;
        const sub = sec.drug.sub_sections.find((ss) => ss.id === subId);
        if (!sub) return;
        if (sub.label === label) return;
        snapshot(s);
        const sec2 = findSection(s.fiche, sectionId);
        if (sec2?.type !== 'drug_card') return;
        const sub2 = sec2.drug.sub_sections.find((ss) => ss.id === subId);
        if (sub2) sub2.label = label;
      }),

    updateDrugSubsectionBulletText: (sectionId, subId, bulletId, text) =>
      set((s) => {
        if (!s.fiche) return;
        const sec = findSection(s.fiche, sectionId);
        if (sec?.type !== 'drug_card') return;
        const sub = sec.drug.sub_sections.find((ss) => ss.id === subId);
        if (!sub) return;
        const target = findBullet(sub.bullets, bulletId);
        if (!target) return;
        if (target.text === text) return;
        snapshot(s);
        const sec2 = findSection(s.fiche, sectionId);
        if (sec2?.type !== 'drug_card') return;
        const sub2 = sec2.drug.sub_sections.find((ss) => ss.id === subId);
        if (!sub2) return;
        const b = findBullet(sub2.bullets, bulletId);
        if (b) b.text = text;
      }),

    updateMnemonicText: (sectionId, text) =>
      set((s) => {
        if (!s.fiche) return;
        const sec = findSection(s.fiche, sectionId);
        if (sec?.type !== 'mnemonic') return;
        if (sec.text === text) return;
        snapshot(s);
        const sec2 = findSection(s.fiche, sectionId);
        if (sec2?.type === 'mnemonic') sec2.text = text;
      }),

    updateWarningText: (sectionId, text) =>
      set((s) => {
        if (!s.fiche) return;
        const sec = findSection(s.fiche, sectionId);
        if (sec?.type !== 'warning') return;
        if (sec.text === text) return;
        snapshot(s);
        const sec2 = findSection(s.fiche, sectionId);
        if (sec2?.type === 'warning') sec2.text = text;
      }),

    updateClinicalPatient: (sectionId, text) =>
      set((s) => {
        if (!s.fiche) return;
        const sec = findSection(s.fiche, sectionId);
        if (sec?.type !== 'clinical_case') return;
        if (sec.patient === text) return;
        snapshot(s);
        const sec2 = findSection(s.fiche, sectionId);
        if (sec2?.type === 'clinical_case') sec2.patient = text;
      }),

    updateClinicalQuestion: (sectionId, text) =>
      set((s) => {
        if (!s.fiche) return;
        const sec = findSection(s.fiche, sectionId);
        if (sec?.type !== 'clinical_case') return;
        if (sec.question === text) return;
        snapshot(s);
        const sec2 = findSection(s.fiche, sectionId);
        if (sec2?.type === 'clinical_case') sec2.question = text;
      }),

    addDiagramNode: (diagramId, node) => {
      const nodeId = node.id ?? makeId('n');
      set((s) => {
        if (!s.fiche) return;
        const d = s.fiche.diagrams.find((x) => x.id === diagramId);
        if (!d) return;
        snapshot(s);
        const d2 = s.fiche.diagrams.find((x) => x.id === diagramId);
        if (!d2) return;
        d2.nodes.push({ ...node, id: nodeId } as DiagramNode);
      });
      return nodeId;
    },

    updateDiagramNodePosition: (diagramId, nodeId, x, y) =>
      set((s) => {
        if (!s.fiche) return;
        const d = s.fiche.diagrams.find((x) => x.id === diagramId);
        if (!d) return;
        const n = d.nodes.find((x) => x.id === nodeId);
        if (!n) return;
        if (n.position.x === x && n.position.y === y) return;
        // Do NOT snapshot every drag tick — frequent position updates would
        // flood the history. We snapshot on the *first* position change in a
        // drag (when past.length is unchanged from the start of the drag).
        // For simplicity, we always snapshot here and dedupe later if needed.
        snapshot(s);
        const d2 = s.fiche.diagrams.find((x) => x.id === diagramId);
        const n2 = d2?.nodes.find((x) => x.id === nodeId);
        if (n2) n2.position = { x, y };
      }),

    updateDiagramNodeLabel: (diagramId, nodeId, label) =>
      set((s) => {
        if (!s.fiche) return;
        const d = s.fiche.diagrams.find((x) => x.id === diagramId);
        if (!d) return;
        const n = d.nodes.find((x) => x.id === nodeId);
        if (!n || n.label === label) return;
        snapshot(s);
        const d2 = s.fiche.diagrams.find((x) => x.id === diagramId);
        const n2 = d2?.nodes.find((x) => x.id === nodeId);
        if (n2) n2.label = label;
      }),

    updateDiagramNodeKind: (diagramId, nodeId, kind) =>
      set((s) => {
        if (!s.fiche) return;
        const d = s.fiche.diagrams.find((x) => x.id === diagramId);
        if (!d) return;
        const n = d.nodes.find((x) => x.id === nodeId);
        if (!n || n.type === kind) return;
        snapshot(s);
        const d2 = s.fiche.diagrams.find((x) => x.id === diagramId);
        const n2 = d2?.nodes.find((x) => x.id === nodeId);
        if (n2) n2.type = kind;
      }),

    updateDiagramNodeColor: (diagramId, nodeId, color) =>
      set((s) => {
        if (!s.fiche) return;
        const d = s.fiche.diagrams.find((x) => x.id === diagramId);
        if (!d) return;
        const n = d.nodes.find((x) => x.id === nodeId);
        if (!n || n.color === color) return;
        snapshot(s);
        const d2 = s.fiche.diagrams.find((x) => x.id === diagramId);
        const n2 = d2?.nodes.find((x) => x.id === nodeId);
        if (n2) n2.color = color;
      }),

    removeDiagramNode: (diagramId, nodeId) =>
      set((s) => {
        if (!s.fiche) return;
        const d = s.fiche.diagrams.find((x) => x.id === diagramId);
        if (!d) return;
        if (!d.nodes.some((n) => n.id === nodeId)) return;
        snapshot(s);
        const d2 = s.fiche.diagrams.find((x) => x.id === diagramId);
        if (!d2) return;
        d2.nodes = d2.nodes.filter((n) => n.id !== nodeId);
        // Also remove any edges that referenced this node
        d2.edges = d2.edges.filter((e) => e.source !== nodeId && e.target !== nodeId);
      }),

    addDiagramEdge: (diagramId, edge) => {
      const edgeId = edge.id ?? makeId('e');
      const state = get();
      const d = state.fiche?.diagrams.find((x) => x.id === diagramId);
      if (!d) return null;
      const valid =
        d.nodes.some((n) => n.id === edge.source) && d.nodes.some((n) => n.id === edge.target);
      if (!valid) return null;
      set((s) => {
        if (!s.fiche) return;
        const dd = s.fiche.diagrams.find((x) => x.id === diagramId);
        if (!dd) return;
        snapshot(s);
        const d2 = s.fiche.diagrams.find((x) => x.id === diagramId);
        if (!d2) return;
        d2.edges.push({ ...edge, id: edgeId });
      });
      return edgeId;
    },

    updateDiagramEdgeKind: (diagramId, edgeId, kind) =>
      set((s) => {
        if (!s.fiche) return;
        const d = s.fiche.diagrams.find((x) => x.id === diagramId);
        if (!d) return;
        const e = d.edges.find((x) => x.id === edgeId);
        if (!e || e.kind === kind) return;
        snapshot(s);
        const d2 = s.fiche.diagrams.find((x) => x.id === diagramId);
        const e2 = d2?.edges.find((x) => x.id === edgeId);
        if (e2) e2.kind = kind;
      }),

    removeDiagramEdge: (diagramId, edgeId) =>
      set((s) => {
        if (!s.fiche) return;
        const d = s.fiche.diagrams.find((x) => x.id === diagramId);
        if (!d) return;
        if (!d.edges.some((e) => e.id === edgeId)) return;
        snapshot(s);
        const d2 = s.fiche.diagrams.find((x) => x.id === diagramId);
        if (d2) d2.edges = d2.edges.filter((e) => e.id !== edgeId);
      }),

    updateDiagramTitle: (diagramId, title) =>
      set((s) => {
        if (!s.fiche) return;
        const d = s.fiche.diagrams.find((x) => x.id === diagramId);
        if (!d || d.title === title) return;
        snapshot(s);
        const d2 = s.fiche.diagrams.find((x) => x.id === diagramId);
        if (d2) d2.title = title;
      }),
  })),
);

function reidNestedBullets(section: FicheSection): void {
  if (section.type === 'intro' || section.type === 'classification') {
    for (const b of section.bullets) b.id = makeId('b');
  } else if (section.type === 'clinical_case') {
    for (const b of section.conduite) b.id = makeId('b');
  } else if (section.type === 'drug_card') {
    for (const b of section.drug.intro_bullets) b.id = makeId('b');
    for (const ss of section.drug.sub_sections) {
      ss.id = makeId('mh');
      for (const b of ss.bullets) b.id = makeId('b');
    }
  }
}
